/**
 * Proyecto: MOGOTAX-SERVER
 * Autor: Megatrack GPS SAS
 * Fecha de creación: 11 de noviembre 2024
 * Licencia: Propiedad Privada
 *
 * Este código es propiedad exclusiva de Megatrack GPS SAS & Mogotax SAS y no puede ser distribuido, copiado, modificado, publicado, sublicenciado ni comercializado sin el consentimiento expreso y por escrito de ambas empresa.
 *
 * Copyright (c) 2025 Megatrack GPS SAS. Todos los derechos reservados.
 */
import React from "react";
import {
  Typography,
  Box,
  Switch,
  Card,
  Fade,
  useTheme,
  useMediaQuery,
  Chip,
} from "@mui/material";
import {
  UnifiedContainer,
  UnifiedLogo,
  UnifiedCardLight,
  UnifiedTitle,
  UnifiedIconButton,
  CenteredLayout,
} from "../../../theme/SharedDesign";
import {
  Battery50,
  BatteryChargingFull,
  GpsFixed,
  GpsNotFixed,
  VolumeUp,
  VolumeOff,
  ArrowBack,
  Settings as SettingsIcon,
  Notifications,
  NotificationsOff,
  Refresh,
  PauseCircleOutline,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { useSettings } from "../../../SettingsContext";
import useNotifications from "../../../hooks/useNotifications";
import logo from "../../../Assets/img/logo.webp";
import { styled } from '@mui/material/styles';

// Card de configuración con estilo claro
const SettingCard = styled(Card)(({ theme, isActive }) => ({
  background: isActive 
    ? 'rgba(10, 46, 92, 0.1)' 
    : 'rgba(255, 255, 255, 0.8)',
  backdropFilter: 'blur(10px)',
  border: isActive 
    ? '2px solid rgba(10, 46, 92, 0.4)' 
    : '1px solid rgba(10, 46, 92, 0.2)',
  borderRadius: 12,
  padding: theme.spacing(2),
  marginBottom: theme.spacing(2),
  cursor: 'pointer',
  transition: 'all 0.3s ease',
  position: 'relative',
  zIndex: 10,
  
  '&:hover': {
    background: 'rgba(10, 46, 92, 0.15)',
    borderColor: 'rgba(10, 46, 92, 0.5)',
    transform: 'translateY(-2px)',
    boxShadow: '0 8px 20px rgba(10, 46, 92, 0.3)',
  },
}));

// Etiqueta de estado
const StatusChip = styled(Chip)(({ theme, status }) => ({
  backgroundColor: status === 'active' 
    ? 'rgba(76, 175, 80, 0.2)' 
    : 'rgba(244, 67, 54, 0.2)',
  color: status === 'active' ? '#4caf50' : '#f44336',
  fontWeight: 600,
  fontSize: '0.75rem',
  border: `1px solid ${status === 'active' ? '#4caf50' : '#f44336'}`,
}));

function Settings() {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const {
    gpsEnabled,
    batteryOptimization,
    soundEnabled,
    notificationsEnabled,
    autoRefresh,
    enableHighAccuracy,
    toggleGPS,
    toggleBatteryOptimization,
    toggleSound,
    toggleNotifications,
    toggleAutoRefresh,
    toggleHighAccuracy
  } = useSettings();

  // Hook de notificaciones
  const {
    isSupported: notificationsSupported,
    permission: notificationPermission,
    requestPermission: requestNotificationPermission
  } = useNotifications();

  // Información del dispositivo y estado de la batería
  const deviceInfo = {
    battery: navigator.getBattery ? 'Disponible' : 'No disponible',
    location: navigator.geolocation ? 'Disponible' : 'No disponible',
    online: navigator.onLine ? 'Conectado' : 'Desconectado',
  };

  const handleBack = () => {
    navigate('/modules/Dashboard');
  };

  const settingsItems = [
    {
      id: 'gps',
      title: 'Localización GPS',
      description: 'Permite el seguimiento de ubicación en tiempo real',
      icon: gpsEnabled ? <GpsFixed /> : <GpsNotFixed />,
      active: gpsEnabled,
      onToggle: toggleGPS,
      status: gpsEnabled ? 'active' : 'inactive'
    },
    {
      id: 'highAccuracy',
      title: 'Ubicación de Alta Precisión',
      description: 'Usa GPS de alta precisión (consume más batería)',
      icon: enableHighAccuracy ? <GpsFixed /> : <GpsNotFixed />,
      active: enableHighAccuracy,
      onToggle: toggleHighAccuracy,
      status: enableHighAccuracy ? 'active' : 'inactive'
    },
    {
      id: 'notifications',
      title: 'Notificaciones Push',
      description: notificationsSupported
        ? (notificationPermission === 'granted'
          ? 'Recibe notificaciones de nuevos traslados'
          : notificationPermission === 'denied'
            ? 'Permisos denegados. Habilita en configuración del navegador'
            : 'Toca para habilitar notificaciones')
        : 'Notificaciones no soportadas en este navegador',
      icon: notificationsEnabled && notificationPermission === 'granted' ? <Notifications /> : <NotificationsOff />,
      active: notificationsEnabled && notificationPermission === 'granted',
      onToggle: async () => {
        if (notificationPermission !== 'granted') {
          await requestNotificationPermission();
        }
        toggleNotifications();
      },
      status: notificationsEnabled && notificationPermission === 'granted' ? 'active' : 'inactive'
    },
    {
      id: 'sound',
      title: 'Sonidos de Notificación',
      description: 'Reproduce sonidos para alertas importantes',
      icon: soundEnabled ? <VolumeUp /> : <VolumeOff />,
      active: soundEnabled,
      onToggle: toggleSound,
      status: soundEnabled ? 'active' : 'inactive'
    },
    {
      id: 'autoRefresh',
      title: 'Actualización Automática',
      description: 'Actualiza automáticamente la lista de traslados',
      icon: autoRefresh ? <Refresh /> : <PauseCircleOutline />,
      active: autoRefresh,
      onToggle: toggleAutoRefresh,
      status: autoRefresh ? 'active' : 'inactive'
    },
    {
      id: 'battery',
      title: 'Modo Ahorro de Batería',
      description: 'Reduce la frecuencia de actualizaciones para ahorrar batería',
      icon: batteryOptimization ? <BatteryChargingFull /> : <Battery50 />,
      active: batteryOptimization,
      onToggle: toggleBatteryOptimization,
      status: batteryOptimization ? 'active' : 'inactive'
    }
  ];

  return (
    <UnifiedContainer>
      <CenteredLayout isMobile={isMobile} maxWidth={500}>
        <UnifiedLogo src={logo} alt="Mogotax Logo" isMobile={isMobile} />
        
        <UnifiedCardLight sx={{ width: '100%', position: 'relative', zIndex: 1 }} isMobile={isMobile}>
          <UnifiedIconButton onClick={handleBack} size={isMobile ? 'medium' : 'large'}>
            <ArrowBack />
          </UnifiedIconButton>

          <UnifiedTitle variant="h4" isMobile={isMobile}>
            <SettingsIcon sx={{ fontSize: isMobile ? '1.6rem' : '2rem', color: '#0a2e5c' }} />
            <span style={{ color: '#0a2e5c' }}>Configuración</span>
          </UnifiedTitle>

          {/* Información del dispositivo */}
          <Box sx={{ mb: 3, position: 'relative', zIndex: 10 }}>
            <Typography variant="h6" sx={{ 
              color: '#0a2e5c', 
              fontWeight: 600, 
              mb: 2,
              fontSize: isMobile ? '1.1rem' : '1.25rem'
            }}>
              Estado del Dispositivo
            </Typography>
            
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              <StatusChip 
                label={`GPS: ${deviceInfo.location}`} 
                status={deviceInfo.location === 'Disponible' ? 'active' : 'inactive'}
                size="small"
              />
              <StatusChip 
                label={`Conexión: ${deviceInfo.online}`} 
                status={deviceInfo.online === 'Conectado' ? 'active' : 'inactive'}
                size="small"
              />
              <StatusChip 
                label={`Batería: ${deviceInfo.battery}`} 
                status={deviceInfo.battery === 'Disponible' ? 'active' : 'inactive'}
                size="small"
              />
            </Box>
          </Box>

          {/* Configuraciones */}
          <Box sx={{ position: 'relative', zIndex: 10 }}>
            <Typography variant="h6" sx={{ 
              color: '#0a2e5c', 
              fontWeight: 600, 
              mb: 2,
              fontSize: isMobile ? '1.1rem' : '1.25rem'
            }}>
              Configuraciones
            </Typography>

            {settingsItems.map((item, index) => (
              <Fade in={true} timeout={500 * (index + 1)} key={item.id}>
                <SettingCard isActive={item.active}>
                  <Box sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'space-between'
                  }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flex: 1 }}>
                      <Box sx={{ 
                        color: item.active ? '#0a2e5c' : 'rgba(10, 46, 92, 0.7)',
                        fontSize: '1.5rem',
                        display: 'flex',
                        alignItems: 'center'
                      }}>
                        {item.icon}
                      </Box>
                      
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="subtitle1" sx={{ 
                          fontWeight: 600, 
                          color: '#0a2e5c',
                          fontSize: isMobile ? '0.95rem' : '1rem'
                        }}>
                          {item.title}
                        </Typography>
                        <Typography variant="body2" sx={{ 
                          color: 'rgba(10, 46, 92, 0.7)',
                          fontSize: isMobile ? '0.8rem' : '0.85rem',
                          lineHeight: 1.3
                        }}>
                          {item.description}
                        </Typography>
                      </Box>
                    </Box>

                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <StatusChip 
                        label={item.active ? 'Activo' : 'Inactivo'} 
                        status={item.status}
                        size="small"
                      />
                      <Switch
                        checked={item.active}
                        onChange={item.onToggle}
                        sx={{
                          '& .MuiSwitch-switchBase.Mui-checked': {
                            color: '#4caf50',
                          },
                          '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                            backgroundColor: '#4caf50',
                          },
                          '& .MuiSwitch-track': {
                            backgroundColor: 'rgba(10, 46, 92, 0.3)',
                          },
                        }}
                      />
                    </Box>
                  </Box>
                </SettingCard>
              </Fade>
            ))}
          </Box>

          {/* Información adicional */}
          <Box sx={{ 
            mt: 3, 
            p: 2, 
            background: 'rgba(10, 46, 92, 0.05)',
            borderRadius: 2,
            border: '1px solid rgba(10, 46, 92, 0.1)',
            position: 'relative',
            zIndex: 10
          }}>
            <Typography variant="caption" sx={{ 
              color: 'rgba(10, 46, 92, 0.8)',
              fontSize: '0.75rem',
              textAlign: 'center',
              display: 'block'
            }}>
              Las configuraciones se aplicarán automáticamente y se guardarán en tu dispositivo.
            </Typography>
          </Box>
        </UnifiedCardLight>
      </CenteredLayout>
    </UnifiedContainer>
  );
}

export default Settings;